import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPhone, FiCheck, FiAlertCircle, FiDollarSign } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { api } from '../../utils/api';

interface ProvisioningModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  selectedNumber: {
    number: string;
    formatted: string;
    region: {
      name: string;
      state: string;
      country: string;
    };
    monthlyFee: number;
    capabilities: string[];
    provider: 'telnyx' | 'twilio';
  } | null;
  onSuccess: () => void;
}

interface Assistant {
  id: string;
  name: string;
  status?: string;
}

export const ProvisioningModal: React.FC<ProvisioningModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  selectedNumber,
  onSuccess
}) => {
  const [friendlyName, setFriendlyName] = useState('');
  const [selectedAssistant, setSelectedAssistant] = useState<string>('');
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [loadingAssistants, setLoadingAssistants] = useState(false);

  useEffect(() => {
    if (isOpen && selectedNumber) {
      setFriendlyName(`${selectedNumber.region.name} Line`);
      loadAssistants();
    }
  }, [isOpen, selectedNumber]);

  const loadAssistants = async () => {
    setLoadingAssistants(true);
    try {
      const response = await api.get('/assistants');
      setAssistants(response.data.assistants || response.data || []);
    } catch (error) {
      console.error('Failed to load assistants:', error);
      toast.error('Failed to load assistants');
    } finally {
      setLoadingAssistants(false);
    }
  };

  const handleProvision = async () => {
    if (!selectedNumber) return;

    setIsProvisioning(true);

    try {
      const response = await api.post(
        `/organizations/${organizationId}/phone-numbers/provision`,
        {
          phoneNumber: selectedNumber.number,
          provider: selectedNumber.provider,
          assistantId: selectedAssistant || undefined,
          friendlyName: friendlyName || selectedNumber.formatted,
          capabilities: selectedNumber.capabilities
        }
      );

      toast.success(
        `Successfully provisioned ${selectedNumber.formatted}!`,
        { duration: 4000 }
      );

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Provisioning error:', error);
      toast.error(
        error.response?.data?.error || 'Failed to provision phone number'
      );
    } finally {
      setIsProvisioning(false);
    }
  };

  if (!selectedNumber) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-slate-800 rounded-xl border border-slate-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-700">
                <div>
                  <h2 className="text-2xl font-bold text-white">Provision Phone Number</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Configure and purchase your new number
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FiX className="text-2xl" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Selected Number Info */}
                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-4 border border-blue-500/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <FiPhone className="text-blue-400 text-xl" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-xl">{selectedNumber.formatted}</p>
                        <p className="text-gray-400 text-sm">
                          {selectedNumber.region.name}, {selectedNumber.region.state}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold text-2xl">
                        ${selectedNumber.monthlyFee.toFixed(2)}
                      </p>
                      <p className="text-gray-400 text-sm">per month</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    {selectedNumber.capabilities.map((cap) => (
                      <span
                        key={cap}
                        className="text-xs bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full"
                      >
                        {cap.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Friendly Name */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Friendly Name
                  </label>
                  <input
                    type="text"
                    value={friendlyName}
                    onChange={(e) => setFriendlyName(e.target.value)}
                    placeholder="e.g., Customer Support Line"
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    A human-readable name to identify this number
                  </p>
                </div>

                {/* Assistant Selection */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Assign to Assistant <span className="text-gray-500">(Optional)</span>
                  </label>
                  <select
                    value={selectedAssistant}
                    onChange={(e) => setSelectedAssistant(e.target.value)}
                    disabled={loadingAssistants}
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none disabled:opacity-50"
                  >
                    <option value="">No assistant (configure later)</option>
                    {assistants.map((assistant) => (
                      <option key={assistant.id} value={assistant.id}>
                        {assistant.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-gray-500 text-xs mt-1">
                    Route incoming calls to a specific AI assistant
                  </p>
                </div>

                {/* Billing Info */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <FiDollarSign className="text-yellow-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-yellow-300 font-medium mb-2">Billing Information</p>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Monthly fee: ${selectedNumber.monthlyFee.toFixed(2)}/month</li>
                        <li>• Billed starting today, pro-rated for partial month</li>
                        <li>• Additional charges apply for calls and SMS usage</li>
                        <li>• Can be canceled anytime from the dashboard</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Important Notice */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <FiAlertCircle className="text-blue-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-blue-300 font-medium mb-2">Important</p>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Number will be active within 1-2 minutes</li>
                        <li>• Configure voicemail and routing after provisioning</li>
                        <li>• SMS requires 10DLC registration for compliance</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-slate-700">
                <button
                  onClick={onClose}
                  disabled={isProvisioning}
                  className="px-6 py-3 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProvision}
                  disabled={isProvisioning}
                  className="px-6 py-3 bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 text-white rounded-lg transition-all flex items-center gap-2"
                >
                  {isProvisioning ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Provisioning...
                    </>
                  ) : (
                    <>
                      <FiCheck />
                      Provision Number
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

